import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to handle image URLs
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${BACKEND_URL}${url}`;
};

const AdminAdvertisements = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    link: '',
    display_order: 0,
    is_active: true,
    image: null
  });
  const { toast } = useToast();

  useEffect(() => {
    loadAds();
  }, []);

  const loadAds = async () => {
    try {
      const response = await apiService.admin.getAdvertisements();
      setAds(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Gagal memuat data iklan',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('link', formData.link);
      formDataToSend.append('display_order', formData.display_order);
      formDataToSend.append('is_active', formData.is_active);
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      if (editingAd) {
        await apiService.admin.updateAdvertisement(editingAd.id, formDataToSend);
        toast({
          title: 'Berhasil',
          description: 'Tersimpan'
        });
      } else {
        await apiService.admin.createAdvertisement(formDataToSend);
        toast({
          title: 'Berhasil',
          description: 'Tersimpan'
        });
      }
      
      loadAds();
      closeModal();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Gagal menyimpan iklan',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus iklan ini?')) return;
    
    try {
      await apiService.admin.deleteAdvertisement(id);
      toast({
        title: 'Berhasil',
        description: 'Iklan berhasil dihapus'
      });
      loadAds();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Gagal menghapus iklan',
        variant: 'destructive'
      });
    }
  };

  const openModal = (ad = null) => {
    if (ad) {
      setEditingAd(ad);
      setFormData({
        title: ad.title,
        link: ad.link,
        display_order: ad.display_order,
        is_active: ad.is_active,
        image: null
      });
    } else {
      setEditingAd(null);
      setFormData({
        title: '',
        link: '',
        display_order: 0,
        is_active: true,
        image: null
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAd(null);
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Kelola Iklan</h2>
          <button
            onClick={() => openModal()}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Tambah Iklan</span>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">Memuat...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ads.map((ad) => (
              <div key={ad.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <img src={getImageUrl(ad.image)} alt={ad.title} className="w-full h-32 object-cover" />
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">{ad.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">Link: {ad.link}</p>
                  <div className="flex justify-between items-center">
                    <span className={`px-2 py-1 text-xs rounded-full ${ad.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {ad.is_active ? 'Aktif' : 'Nonaktif'}
                    </span>
                    <div className="flex space-x-2">
                      <button onClick={() => openModal(ad)} className="text-blue-600 hover:text-blue-800">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(ad.id)} className="text-red-600 hover:text-red-800">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {editingAd ? 'Edit Iklan' : 'Tambah Iklan'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Judul</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Link</label>
                  <input
                    type="text"
                    value={formData.link}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gambar</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                    className="w-full px-4 py-2 border rounded-md"
                    required={!editingAd}
                  />
                </div>
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
                  >
                    {editingAd ? 'Update' : 'Simpan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAdvertisements;